import type {
  CitationGraphEmptyReason,
  ConceptDiagram,
  ConceptDiagramKind,
  ConceptDiagramRole,
  ContentBlock,
  Paper,
  VisualSummaryItem,
} from '../contracts/types.ts';
import { hasResolvedPaperLocator } from './reportStructure.ts';
export type { VisualSummaryItem } from '../contracts/types.ts';
export {
  REPORT_OVERVIEW_LABELS,
  REPORT_SECTION_HEADINGS,
  centralClaimFromBlocks,
  isPublishableReportOverview,
  parseReportOverviewBlock,
  reportOverviewItems,
  reportSectionBlocks,
  splitReportBlocks,
  type ReportOverviewItem,
} from './reportStructure.ts';

export const VISUAL_SUMMARY_PREFIX = '一枚図解：研究の全体像';
export const ELI5_EYEBROW = 'OVERVIEW';
export const ELI5_TITLE = '研究の要点';
export const ELI5_DESCRIPTION = '困りごとから、結果とまだ言えないことまでをまとめています。';
export const VISUAL_SUMMARY_LABELS = [
  '困りごと',
  'この研究の工夫',
  '確かめ方',
  '分かったこと',
  'まだ言えないこと',
] as const;

const CHAPTER_OUTLINE_TERM = /^(?:背景|方法|結果|考察|結論|限界|目次|章|節)(?:について)?$/;

function isChapterOutlineValue(text: string): boolean {
  const parts = text.split(/[→⇒]/).map((part) => part.trim()).filter(Boolean);
  return parts.length > 0 && parts.every((part) => CHAPTER_OUTLINE_TERM.test(part));
}

/**
 * 取得範囲と数式の省略理由はメタデータで示す。
 * 旧資産に残る前置き・語尾の揺れも、可視本文には再掲しない。
 */
function isUnhelpfulAutoNote(text: string): boolean {
  const compact = text.replace(/[\s\u3000]+/g, '');
  return /(?:この)?解説.{0,40}要旨.{0,40}基づ/.test(compact)
    || /数式.{0,100}(?:抽出結果|数値の?根拠).{0,100}省略/.test(compact);
}

/** 重複するラベルだけを外し、後ろの実質的な考察は残す。 */
export function displayVisibleText(text: string | undefined): string {
  const value = text?.trim() ?? '';
  if (isUnhelpfulAutoNote(value)) return '';
  return value.replace(
    /^(?:(?:注|補足|備考|参考|編集部より)\s*[：:]\s*)?(?:ここから|以下(?:の内容)?)は考察(?:・応用案)?(?:ですが|です|となります|に移ります|を述べます)?[、，。.!！：:\s]*/,
    '',
  ).trim();
}

export function displayBlockItems(block: ContentBlock): string[] {
  return (block.items ?? []).map(displayVisibleText).filter((item) => item.length > 0);
}

/** 共有表示変換後に本文が空になる旧ブロックを識別する。 */
export function isUnhelpfulEditorialNote(block: ContentBlock): boolean {
  if (block.kind === 'list' || block.kind === 'takeaways') {
    return (block.items?.length ?? 0) > 0 && displayBlockItems(block).length === 0;
  }
  return Boolean(block.text?.trim()) && displayVisibleText(block.text).length === 0;
}

export function isVisualSummaryText(text: string | undefined): boolean {
  return text?.trimStart().startsWith(VISUAL_SUMMARY_PREFIX) ?? false;
}

/** 章名ではなく、研究の意味を表す短い5要素だけを一枚図解へ渡す。 */
export function parseVisualSummary(text: string | undefined): VisualSummaryItem[] {
  if (!text || !isVisualSummaryText(text)) return [];
  return text
    .split('\n')
    .slice(1)
    .flatMap((line) => {
      const match = /^［([^］]+)］\s*(.+)$/.exec(line.trim());
      return match?.[1] && match[2]
        ? [{ label: match[1].trim(), text: match[2].trim() }]
        : [];
    });
}

/** 旧要点の詰め替えではなく、生成・検査する5要素が揃った図解だけを表示する。 */
export function isCompleteVisualSummary(text: string | undefined): boolean {
  const lines = text?.trim().split('\n').map((line) => line.trim()).filter(Boolean) ?? [];
  if (lines.length !== VISUAL_SUMMARY_LABELS.length + 1 || lines[0] !== VISUAL_SUMMARY_PREFIX) return false;
  const items = parseVisualSummary(text);
  return items.length === VISUAL_SUMMARY_LABELS.length
    && items.every((item, index) =>
      item.label === VISUAL_SUMMARY_LABELS[index]
      && [...item.text].length <= 45
      && !isChapterOutlineValue(item.text),
    );
}

/** 公開する一枚図解の共通ゲート。生成・Notion・D1・Webで同じ条件を使う。 */
export function isPublishableVisualSummaryBlock(block: ContentBlock | undefined): block is ContentBlock {
  return block != null
    && typeof block === 'object'
    && block.kind === 'callout'
    && block.provenance === 'paper'
    && typeof block.text === 'string'
    && hasResolvedPaperLocator(block.sources)
    && isCompleteVisualSummary(block.text);
}

const CONCEPT_DIAGRAM_KINDS: readonly ConceptDiagramKind[] = ['flow', 'cycle', 'compare'];
const CONCEPT_DIAGRAM_ROLES: readonly ConceptDiagramRole[] = [
  'input', 'process', 'output', 'evidence', 'comparison', 'limitation',
];
const CONFIDENCE_VALUES = ['confirmed', 'unverified', 'needs_check'];

/**
 * 概念図ノード ID の形。英数字で始まり、英数字とハイフンだけ、64 文字まで。
 * 生成 Schema（prompts.ts）・生成後の実体化（text.ts）・verify.ts・Notion の parser・Worker の同期検査が
 * すべてこの1つを見る。どこか1か所だけ緩いと、Notion 監査を通った図が D1 同期で 400 になり
 * 当日分の公開がまとめて止まる（同期 batch は原子的）。
 */
export const CONCEPT_DIAGRAM_NODE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/;

/**
 * 公開する概念図の共通ゲート。Web はこれで描くかどうかを決める。
 *
 * 見るのは構造・provenance='paper'・各ノードの原文出典・ノード ID の形・辺の ID 参照整合だけ。
 * ノード数やラベル長のしきい値は config の値なので verify.ts 側で見る
 * （Web は config を持たないため、ここへしきい値を持ち込むと二重管理になる）。
 */
export function isPublishableConceptDiagram(diagram: unknown): diagram is ConceptDiagram {
  if (diagram == null || typeof diagram !== 'object') return false;
  const candidate = diagram as Record<string, unknown>;
  if (candidate['provenance'] !== 'paper') return false;
  if (typeof candidate['title'] !== 'string' || candidate['title'].trim().length === 0) return false;
  if (typeof candidate['caption'] !== 'string') return false;
  if (!CONCEPT_DIAGRAM_KINDS.includes(candidate['kind'] as ConceptDiagramKind)) return false;
  if (!CONFIDENCE_VALUES.includes(candidate['confidence'] as string)) return false;
  const nodes = candidate['nodes'];
  const edges = candidate['edges'];
  if (!Array.isArray(nodes) || nodes.length === 0 || !Array.isArray(edges)) return false;

  const ids = new Set<string>();
  for (const node of nodes as unknown[]) {
    if (node == null || typeof node !== 'object') return false;
    const record = node as Record<string, unknown>;
    const id = record['id'];
    if (typeof id !== 'string' || !CONCEPT_DIAGRAM_NODE_ID_PATTERN.test(id) || ids.has(id)) return false;
    if (typeof record['label'] !== 'string' || record['label'].trim().length === 0) return false;
    if (!CONCEPT_DIAGRAM_ROLES.includes(record['role'] as ConceptDiagramRole)) return false;
    if (record['note'] !== undefined && typeof record['note'] !== 'string') return false;
    if (!hasResolvedPaperLocator(record['sources'])) return false;
    ids.add(id);
  }
  for (const edge of edges as unknown[]) {
    if (edge == null || typeof edge !== 'object') return false;
    const record = edge as Record<string, unknown>;
    if (typeof record['from'] !== 'string' || typeof record['to'] !== 'string') return false;
    if (!ids.has(record['from']) || !ids.has(record['to'])) return false;
    if (record['label'] !== undefined && typeof record['label'] !== 'string') return false;
  }
  return true;
}

/** 旧版の「見出し一覧」は図として見せない。本文そのものは後段に残る。 */
export function isLegacyOutline(text: string | undefined): boolean {
  return /^(?:図解[:：]まずひと目でつかむ|研究の流れ（図解）)/.test(text?.trim() ?? '');
}

export interface OriginalFigureInfo {
  imageUrl: string;
  sourceUrl: string;
}

/** 権利とURL規則を確認できる Springer Nature の CC BY 論文だけ Figure 1 を表示する。 */
export function originalFigureFor(paper: Paper): OriginalFigureInfo | null {
  const doi = paper.identifiers.doi?.toLowerCase();
  if (!doi || !paper.license.id?.toLowerCase().startsWith('cc-by')) return null;
  const match = /^10\.1038\/(s(\d{5})-(\d{3})-0*(\d+)-\d+)$/.exec(doi);
  if (!match) return null;
  const [, suffix, journal, shortYear, articleNumber] = match;
  const year = 2000 + Number(shortYear);
  return {
    imageUrl:
      `https://media.springernature.com/lw685/springer-static/image/art%3A${encodeURIComponent(`10.1038/${suffix}`)}` +
      `/MediaObjects/${journal}_${year}_${Number(articleNumber)}_Fig1_HTML.png`,
    sourceUrl: `https://www.nature.com/articles/${suffix}/figures/1`,
  };
}

export function displayBlockText(block: ContentBlock): string {
  return displayVisibleText(block.text);
}

const PAPER_MAP_SECTIONS = [
  ['claim', '結論', 'この論文がいちばん伝えたいこと'],
  ['need', '必要性', 'なぜこの研究が必要？'],
  ['novelty', '新規性', '何が新しかった？'],
  ['method', '方法', 'どう確かめた？'],
  ['result', '結果', '何が分かった？'],
  ['limit', '限界', 'どこまで信じられる？'],
  ['application', '応用', '実際に使うなら'],
] as const;

export interface PaperMapItem {
  key: typeof PAPER_MAP_SECTIONS[number][0];
  label: string;
  headingId: string;
  text: string;
  provenance: ContentBlock['provenance'];
  confidence: ContentBlock['confidence'];
}

/** 検証済みの本文7節をそのまま一枚図へ並べる。新しい主張は生成しない。 */
export function paperMapItemsFromBlocks(blocks: ContentBlock[]): PaperMapItem[] {
  return PAPER_MAP_SECTIONS.flatMap(([key, label, heading]) => {
    const index = blocks.findIndex((block) => block.kind === 'heading' && block.text?.trim() === heading);
    const body = index < 0 ? undefined : blocks[index + 1];
    const text = body ? displayBlockText(body) || displayBlockItems(body)[0] || '' : '';
    return body && text
      ? [{ key, label, headingId: blocks[index]!.id, text, provenance: body.provenance, confidence: body.confidence }]
      : [];
  });
}

export interface UnderstandingCheckItem {
  question: string;
  answer: string;
  provenance: ContentBlock['provenance'];
  sources: ContentBlock['sources'];
  confidence: ContentBlock['confidence'];
}

/**
 * 理解確認に使う本文3節。**並びが「問い｜答え」list の並びの正本。**
 *
 * 生成側（ensureUnderstandingCheck と部分修理の quiz）はこの並びで list を書く。
 * 画面側もこの並びで問いと節を対応づける（understandingCheckItems）。
 */
const UNDERSTANDING_CHECK_SECTIONS = [
  ['この論文が最も伝えたいことは？', 'この論文がいちばん伝えたいこと', true],
  ['この研究の新しかった工夫は？', '何が新しかった？', true],
  ['この結果からまだ言えないことは？', 'どこまで信じられる？', false],
] as const;

/** 導出できた項目と、それがどの節から来たか。節が欠けると導出は詰まるので添字がずれる。 */
interface DerivedUnderstandingCheck {
  item: UnderstandingCheckItem;
  /** UNDERSTANDING_CHECK_SECTIONS 上の位置。 */
  sectionIndex: number;
}

function derivedUnderstandingChecks(blocks: ContentBlock[]): DerivedUnderstandingCheck[] {
  const fromSection = (heading: string, paperOnly: boolean): ContentBlock | undefined => {
    const start = blocks.findIndex((block) => block.kind === 'heading' && block.text?.trim() === heading);
    if (start < 0) return undefined;
    const end = blocks.findIndex((block, index) => index > start && block.kind === 'heading');
    const candidates = blocks.slice(start + 1, end < 0 ? undefined : end).filter((block) =>
      Boolean(displayBlockText(block) || displayBlockItems(block)[0]),
    );
    return candidates.find((block) => block.provenance === 'paper') ?? (paperOnly ? undefined : candidates[0]);
  };
  return UNDERSTANDING_CHECK_SECTIONS.flatMap(([question, heading, paperOnly], sectionIndex) => {
    const block = fromSection(heading, paperOnly);
    const answer = block
      ? (displayBlockText(block) || displayBlockItems(block)[0] || '').replace(/\s+/g, ' ').trim()
      : '';
    return block && answer
      ? [{
        item: { question, answer, provenance: block.provenance, sources: block.sources, confidence: block.confidence },
        sectionIndex,
      }]
      : [];
  });
}

/** 独立生成した一般文ではなく、既に検証済みの中心主張・新規性・限界を理解確認に使う。 */
export function understandingCheckFromBlocks(blocks: ContentBlock[]): UnderstandingCheckItem[] {
  return derivedUnderstandingChecks(blocks).map((derived) => derived.item);
}

/**
 * 画面に出す理解確認。答えは常に検証済みの本文節から導く（understandingCheckFromBlocks）。
 * 問いは、生成側（ensureUnderstandingCheck）が「問い｜答え」の list に残した論文固有の問いを優先する。
 *
 * **対応づけは list の添字ではなく節の位置で行う。** 導出は本文に無い節を詰めて返すので、
 * 例えば「いちばん伝えたいこと」が欠けた論文では導出の0番目が新規性になる。list の0番目
 * （中心主張の問い）をそこへ当てると、新規性の答えに中心主張の問いが付く。
 * list の件数が3節と合わない・問いが空のときは、その節の既定の問いへ戻す（v18 以前のデータもこの経路）。
 * 問いが既定文と同じなら結果も既定文のままなので、3節そろった既存データの見え方は変わらない。
 */
export function understandingCheckItems(blocks: ContentBlock[], quiz: ContentBlock | undefined): UnderstandingCheckItem[] {
  const derived = derivedUnderstandingChecks(blocks);
  const items = quiz?.kind === 'list' ? (quiz.items ?? []) : [];
  if (items.length !== UNDERSTANDING_CHECK_SECTIONS.length) return derived.map((entry) => entry.item);
  return derived.map(({ item, sectionIndex }) => {
    const question = (items[sectionIndex] ?? '').split('｜')[0]?.trim() ?? '';
    return question.length > 0 ? { ...item, question } : item;
  });
}

/**
 * 引用グラフが無いときの理由。型の正本は src/contracts/types.ts。
 * 既存の import 元を変えずに済むよう、ここからも同じ型を再輸出する。
 */
export type { CitationGraphEmptyReason };

export interface CitationGraphEmptyState {
  title: string;
  detail: string;
  /** 見出しのチップ。断定してよいのは「なし」だけで、それ以外は「未確認」。 */
  chip: string;
}

/** 引用が 0 件だと確認できたときだけ使う。 */
const CITATION_CHIP_NONE = '引用データ: なし';
/** 「無い」とも「取れた」とも言えないときのチップ。 */
const CITATION_CHIP_UNKNOWN = '引用データ: 未確認';

/**
 * 引用グラフが無いときの本文とチップを1か所で決める純関数。
 *
 * 分けて書いていたときは、同じ応答からチップが「なし」と断定し本文が「断定できません」と言う
 * 組み合わせが出ていた（reason='no_edges' かつ truncated が true のとき）。
 * 断定してよいのは「保存できていて、取得も途中で止まっていない（truncated が両方 false）のに
 * 0 件だった」ときだけ。
 *
 * 絞り込みの案内は、理由が付いていない旧 Worker の応答にだけ出す。graph が null のときは
 * ツールバーを描かないので、理由が分かっているのに「絞り込みを外してください」と言うと、
 * 外す手段の無い操作を求めることになる（保存済みの向き設定で最初から絞り込まれている場合）。
 */
export function citationGraphEmptyState(input: {
  reason?: CitationGraphEmptyReason;
  truncated?: { references?: boolean; citedBy?: boolean };
  unfiltered: boolean;
}): CitationGraphEmptyState {
  if (input.reason === undefined && !input.unfiltered) {
    return {
      title: 'この条件に合う引用関係はありません',
      detail: '向きや「解説がある論文のみ」の絞り込みを外すと表示されることがあります。',
      chip: CITATION_CHIP_UNKNOWN,
    };
  }
  if (input.truncated?.references === true || input.truncated?.citedBy === true) {
    return {
      title: '引用データの取得が一部失敗しました',
      detail: '提供元（OpenAlex）からの取得が途中で止まったため、引用の有無を断定できません。',
      chip: CITATION_CHIP_UNKNOWN,
    };
  }
  if (input.reason === 'no_graph_stored') {
    return {
      title: '引用データを保存できていません',
      detail: '収集時に提供元（OpenAlex）への照会が完了しなかったため、この論文の引用関係は表示できません。',
      chip: CITATION_CHIP_UNKNOWN,
    };
  }
  if (input.reason === 'no_edges') {
    return {
      title: 'この論文の引用データは提供元（OpenAlex）に登録がありません',
      detail: '引用データの有無は本文（全文）の取得とは別です。arXiv の新着論文などは、提供元に引用データがまだ無いことがあります。',
      chip: CITATION_CHIP_NONE,
    };
  }
  return {
    title: '引用関係を取得できませんでした',
    detail: '提供元から参考文献・被引用のどちらも得られていません。',
    chip: CITATION_CHIP_UNKNOWN,
  };
}
